import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { ProgressBar } from 'react-loader-spinner';

const ExcelUploadModal = ({ 
  isOpen, 
  onClose, 
  students, 
  subject, 
  classe, 
  onDataProcessed 
}) => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const fileInputRef = useRef(null);

  // Reset modal state
  const resetModal = () => {
    setFile(null);
    setParsedData([]);
    setValidationErrors([]);
    setIsProcessing(false);
    setIsValidating(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const gender = classe?.split("-")[1] === "Boys" ? "male" : "female";

  // Handle modal close
  const handleClose = () => {
    resetModal();
    onClose();
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      if (!['xlsx', 'xls', 'csv'].includes(fileExtension)) {
        toast.error('❌ Please select Excel (.xlsx, .xls) or CSV file');
        return;
      }
      if (selectedFile.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('❌ File size must be less than 5MB');
        return;
      }
      setFile(selectedFile);
      parseFile(selectedFile);
    }
  };

  // Parse Excel/CSV file
  const parseFile = async (file) => {
    setIsValidating(true);
    setValidationErrors([]);

    try {
      const data = await readFile(file);
      const processedData = processExcelData(data);
      setParsedData(processedData);
      
      if (processedData.length === 0) {
        toast.warning('⚠️ No valid data found in the file');
      } else {
        toast.success(`✅ Successfully parsed ${processedData.length} rows`);
      }
    } catch (error) {
      console.error('File parsing error:', error);
      toast.error('❌ Error parsing file. Please check the format.');
    } finally {
      setIsValidating(false);
    }
  };

  // Read file content
  const readFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsBinaryString(file);
    });
  };

  // Process Excel data
  const processExcelData = (data) => {
    if (data.length < 2) {
      setValidationErrors(['File must have at least a header row and one data row']);
      return [];
    }

    const headers = data[0].map(h => h ? h.toString().toLowerCase().trim() : '');
    const processedData = [];
    const errors = [];

    // Find column indices
    const rollIndex = findColumnIndex(headers, ['roll', 'rollno', 'roll no', 'roll number']);
    const idIndex = findColumnIndex(headers, ['id', 'student id', 'studentid', 'phone', 'phone number']);
    const nameIndex = findColumnIndex(headers, ['name', 'student name', 'studentname', 'full name']);
    const writingIndex = findColumnIndex(headers, ['writing', 'written', 'written marks']);
    const mcqIndex = findColumnIndex(headers, ['mcq', 'mcq marks', 'objective']);
    const practicalIndex = findColumnIndex(headers, ['practical', 'practical marks', 'practical marks']);
    const feedbackIndex = findColumnIndex(headers, ['feedback', 'comment', 'remarks']);

    // Validate required columns
    if (idIndex === -1) errors.push('ID column is required');
    if (nameIndex === -1) errors.push('Student Name column not found');
    if (writingIndex === -1) errors.push('Writing Marks column not found');
    if (mcqIndex === -1) errors.push('MCQ Marks column not found');

    if (errors.length > 0) {
      setValidationErrors(errors);
      return [];
    }

    // Process each row
    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      if (!row || row.every(cell => !cell)) continue; // Skip empty rows

      const rowData = {
        rowNumber: i + 1,
        roll: rollIndex !== -1 ? (row[rollIndex] ? row[rollIndex].toString().trim() : '') : '',
        id: idIndex !== -1 ? (row[idIndex] ? row[idIndex].toString().trim() : '') : '',
        name: row[nameIndex] ? row[nameIndex].toString().trim() : '',
        writing: parseFloat(row[writingIndex]) || 0,
        mcq: parseFloat(row[mcqIndex]) || 0,
        practical: practicalIndex !== -1 ? (parseFloat(row[practicalIndex]) || 0) : 0,
        feedback: feedbackIndex !== -1 ? (row[feedbackIndex] ? row[feedbackIndex].toString().trim() : '') : '',
        matchedStudent: null,
        validationErrors: []
      };

      // Validate data
      validateRowData(rowData, errors);
      processedData.push(rowData);
    }

    setValidationErrors(errors);
    return processedData;
  };

  // Find column index by name
  const findColumnIndex = (headers, possibleNames) => {
    for (let i = 0; i < headers.length; i++) {
      for (const name of possibleNames) {
        if (headers[i].includes(name)) {
          return i;
        }
      }
    }
    return -1;
  };

  // Validate row data
  const validateRowData = (rowData, errors) => {
    const rowErrors = [];

    // Validate ID
    if (!rowData.id) {
      rowErrors.push('ID is required');
    }

    // Validate name
    if (!rowData.name) {
      rowErrors.push('Student name is required');
    }

    // Validate marks
    if (rowData.writing < 0) {
      rowErrors.push('Writing marks cannot be negative');
    }
    if (rowData.mcq < 0) {
      rowErrors.push('MCQ marks cannot be negative');
    }
    if (rowData.practical < 0) {
      rowErrors.push('Practical marks cannot be negative');
    }

    // Check if at least one mark is provided
    if (rowData.writing === 0 && rowData.mcq === 0 && rowData.practical === 0) {
      rowErrors.push('At least one mark (Writing, MCQ, or Practical) must be provided');
    }

    rowData.validationErrors = rowErrors;
    if (rowErrors.length > 0) {
      errors.push(`Row ${rowData.rowNumber}: ${rowErrors.join(', ')}`);
    }
  };

  // Match students with database
  const matchStudents = () => {
    const matchedData = parsedData.map(rowData => {
      // Match by ID only (phone number)
      let matchedStudent = null;
      
      if (rowData.id) {
        matchedStudent = students.find(student => 
          student.phoneNumber?.slice(5) === rowData.id && student.class === classe?.split("-")[0]
        );
      }

      return {
        ...rowData,
        matchedStudent
      };
    });

    return matchedData;
  };

  // Handle data processing
  const handleProcessData = () => {
    const matchedData = matchStudents();
    const unmatchedStudents = matchedData.filter(item => !item.matchedStudent);
    
    if (unmatchedStudents.length > 0) {
      const unmatchedNames = unmatchedStudents.map(item => item.name).join(', ');
      toast.warning(`⚠️ ${unmatchedStudents.length} students not found in class: ${unmatchedNames}`);
    }

    // Pass data to parent component
    onDataProcessed(matchedData);
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              📊 Bulk Upload Results
            </h2>
            <p className="text-gray-600">
              Class: <span className="font-semibold">{classe}</span> | 
              Subject: <span className="font-semibold">{subject}</span>
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-blue-800 mb-2">📋 File Requirements:</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Supported formats: .xlsx, .xls, .csv</li>
            <li>• Maximum file size: 5MB</li>
            <li>• Required columns: ID, Name, Writing, MCQ</li>
            <li>• Optional columns: Practical, Feedback</li>
            <li>• First row should contain column headers</li>
          </ul>
        </div>

        {/* File Upload */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Excel/CSV File
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileSelect}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        </div>

        {/* Processing Status */}
        {isValidating && (
          <div className="flex items-center justify-center py-8">
            <ProgressBar
              height="80"
              width="80"
              ariaLabel="progress-bar-loading"
              wrapperStyle={{}}
              wrapperClass="progress-bar-wrapper"
              borderColor="#F4442E"
              barColor="#51E5FF"
            />
            <span className="ml-4 text-gray-600">Processing file...</span>
          </div>
        )}

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-red-800 mb-2">❌ Validation Errors:</h3>
            <ul className="text-sm text-red-700 space-y-1 max-h-32 overflow-y-auto">
              {validationErrors.map((error, index) => (
                <li key={index}>• {error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Data Preview */}
        {parsedData.length > 0 && (
          <div className="mb-6">
            <h3 className="font-semibold text-gray-800 mb-4">
              📋 Data Preview ({parsedData.length} rows)
            </h3>
            <div className="overflow-x-auto max-h-64 border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Row</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Roll</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Writing</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">MCQ</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Practical</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {parsedData.slice(0, 10).map((row, index) => {
                    // Match by ID only (phone number)
                    let matchedStudent = null;
                    
                    if (row.id) {
                      matchedStudent = students.find(student => 
                        student.phoneNumber?.slice(5) === row.id && student.class === classe?.split("-")[0]
                      );
                    }
                    return (
                      <tr key={index} className={matchedStudent ? 'bg-green-50' : 'bg-red-50'}>
                        <td className="px-3 py-2 text-sm text-gray-900">{row.rowNumber}</td>
                        <td className="px-3 py-2 text-sm text-gray-900">{row.id || '-'}</td>
                        <td className="px-3 py-2 text-sm text-gray-900">{row.roll || '-'}</td>
                        <td className="px-3 py-2 text-sm text-gray-900">{row.name}</td>
                        <td className="px-3 py-2 text-sm text-gray-900 text-center">{row.writing}</td>
                        <td className="px-3 py-2 text-sm text-gray-900 text-center">{row.mcq}</td>
                        <td className="px-3 py-2 text-sm text-gray-900 text-center">{row.practical}</td>
                        <td className="px-3 py-2 text-sm text-center">
                          {matchedStudent ? (
                            <span className="text-green-600">✓ Matched</span>
                          ) : (
                            <span className="text-red-600">✗ Not Found</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {parsedData.length > 10 && (
                <div className="text-center py-2 text-sm text-gray-500">
                  ... and {parsedData.length - 10} more rows
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Cancel
          </button>
          {parsedData.length > 0 && (
            <button
              onClick={handleProcessData}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
            >
              Process Data
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExcelUploadModal;
